import React from 'react'
import { showAxiosErrorAsToast, useRequestsWhileMounted } from '../../../request'
import { wrapSetState } from '../../../utils'
import { UserSelector } from '../../../components/userSelector/userSelector'
import { SharedRecipient, RecipientsInfo, ComposeState } from './composeTypes'
import { loadSharedRecipientsByIdentifiers } from '../../../components/userSelector/userSelectorPlugins'

interface ShareRecipientSelectorProps {
  composeState: ComposeState
  loadedRecipients: SharedRecipient[]
  setRecipientsInfo: React.Dispatch<React.SetStateAction<RecipientsInfo>>
  highlightReasonsWeCantSend?: boolean
}

export const ShareRecipientSelector: React.FC<ShareRecipientSelectorProps> = ({
  composeState,
  setRecipientsInfo,
  loadedRecipients,
  highlightReasonsWeCantSend,
}) => {
  const [loading, setLoading] = React.useState<boolean>(false)
  const [allInitialRecipients, setAllInitialRecipients] = React.useState<SharedRecipient[]>(loadedRecipients)
  const makeRequest = useRequestsWhileMounted()
  React.useEffect(() => {
    if (composeState.recipientsInfo.sharedTeacherRecipients.length > 0) return
    setLoading(true)
    makeRequest<{ existingIDs: (string | number)[]; newRecipients: SharedRecipient[] }>(
      '/frontend_api/getTeacherRecipientsForUsers',
      {
        students: composeState.selectedRecipients.filter((r) => !r.group).map((r) => r.id),
        groups: composeState.selectedRecipients
          .filter((r) => !!r.group)
          .filter((r) => r.filterOnTab !== 'mass' || r.groupID.toString().includes('custom-temp'))
          .map((r) => r.groupID),
        mass_groups: composeState.selectedRecipients
          .filter((r) => !!r.group)
          .filter((r) => r.filterOnTab === 'mass' && !r.groupID.toString().includes('custom-temp'))
          .map((r) => r.groupID),
      }
    )
      .then((data) => {
        const foundExistingRecipients = loadedRecipients.filter((r) =>
          data.existingIDs.includes(r.group ? r.groupID : r.id)
        )
        setRecipientsInfo((ri) => ({
          ...ri,
          sharedTeacherRecipients: foundExistingRecipients.concat(data.newRecipients),
        }))
        setAllInitialRecipients((ir) => ir.concat(data.newRecipients))
      })
      .catch(showAxiosErrorAsToast)
      .finally(() => setLoading(false))
  }, []) // preload on mount only

  const recipientsFilter = React.useCallback(
    (r: SharedRecipient) => r.filterOnTab !== 'mass' || composeState.sendTypes.has('mass'),
    [composeState.sendTypes]
  )

  const fetchUsersByIdentifiersForPaste = React.useMemo(() => loadSharedRecipientsByIdentifiers(), [])

  return (
    <UserSelector<SharedRecipient>
      selectedUsers={loading ? [] : composeState.recipientsInfo.sharedTeacherRecipients}
      setSelectedUsers={wrapSetState(setRecipientsInfo, 'sharedTeacherRecipients')}
      url="/frontend_api/getTeacherRecipients"
      disabled={loading}
      loadedUsers={allInitialRecipients}
      placeholder="Search for staff"
      userDescriptor="staff"
      userVisibleFilter={recipientsFilter}
      clearable
      fetchUsersByIdentifiersForPaste={fetchUsersByIdentifiersForPaste}
      invalid={
        highlightReasonsWeCantSend &&
        composeState.recipientsInfo.toSharedTeachers &&
        composeState.recipientsInfo.sharedTeacherRecipients.length === 0
      }
      showTooltipOnBadges
    />
  )
}
